import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SettingsProvider, useSettings } from '@/contexts/SettingsContext';
import TopBar from '@/components/TopBar';
import ActivePage from '@/pages/ActivePage';
import StoragePage from '@/pages/StoragePage';
import ArchivePage from '@/pages/ArchivePage';
import TagsPage from '@/pages/TagsPage';
import AdminPage from '@/pages/AdminPage';
import LandingPage from '@/pages/LandingPage';
import AddTaskDialog from '@/components/AddTaskDialog';
import SettingsDialog from '@/components/SettingsDialog';
import AboutDialog from '@/components/AboutDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { selectedContextId, contextsEnabled, contexts } = useSettings();
  const [activeTab, setActiveTab] = useState('active');
  const [showAddTask, setShowAddTask] = useState(false);
  const [addTaskList, setAddTaskList] = useState(null); // Active list object to pre-fill (or null)
  const [showSettings, setShowSettings] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragHandlePosition, setDragHandlePosition] = useState('right');

  // Keyboard shortcut for adding tasks (Option+Cmd+T on Mac, Ctrl+Alt+T on Windows)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Mac: Option (Alt) + Command (Meta) + T
      // Windows/Linux: Ctrl + Alt + T
      const isTKey = e.key === 't' || e.key === 'T' || e.code === 'KeyT';
      
      const isMacShortcut = e.altKey && e.metaKey && isTKey;
      const isWindowsShortcut = e.ctrlKey && e.altKey && !e.metaKey && isTKey;
      
      if ((isMacShortcut || isWindowsShortcut) && !e.shiftKey) {
        e.preventDefault();
        setAddTaskList(null);
        setShowAddTask(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch colors and settings and apply them
  useEffect(() => {
    const fetchAndApplySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, {
          withCredentials: true
        });
        const primaryColor = response.data.primary_color || '#416ec8';
        const secondaryColor = response.data.secondary_color || '#e9f5fb';
        const handlePosition = response.data.drag_handle_position || 'right';
        
        // Always apply dark mode
        document.documentElement.classList.add('dark');
        
        // Set colors
        document.documentElement.style.setProperty('--color-primary', primaryColor);
        document.documentElement.style.setProperty('--color-secondary', secondaryColor);
        
        setDragHandlePosition(handlePosition);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    if (isAuthenticated) {
      fetchAndApplySettings();
    }
  }, [isAuthenticated]);

  // Fetch tasks (with optional context filtering)
  const fetchTasks = async (contextId = null) => {
    try {
      const params = contextId ? `?context_id=${contextId}` : '';
      const response = await axios.get(`${API}/tasks${params}`, {
        withCredentials: true
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Fetch tags (with optional context filtering)
  const fetchTags = async (contextId = null) => {
    try {
      const params = contextId ? `?context_id=${contextId}` : '';
      const response = await axios.get(`${API}/tags${params}`, {
        withCredentials: true
      });
      if (response.data.length === 0 && !contextId) {
        // Initialize default tags only if no context filter
        await axios.post(`${API}/tags/init-defaults`, {}, {
          withCredentials: true
        });
        const tagsResponse = await axios.get(`${API}/tags${params}`, {
          withCredentials: true
        });
        setTags(tagsResponse.data);
      } else {
        setTags(response.data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Unified data fetch - handles initial load and context/tab changes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchData = async () => {
      if (contextsEnabled) {
        // For context-specific pages (Storage, Archive, Tags), filter by context
        if (activeTab === 'storage' || activeTab === 'archive' || activeTab === 'tags') {
          const contextToUse = selectedContextId || (contexts.length > 0 ? contexts[0].id : null);
          if (contextToUse) {
            await fetchTags(contextToUse);
            await fetchTasks(contextToUse);
          }
        } else {
          // Active page shows all tasks (no context filter)
          await fetchTags();
          await fetchTasks();
        }
      } else {
        // Contexts disabled - fetch all
        await fetchTags();
        await fetchTasks();
      }
      setLoading(false);
    };
    
    fetchData();
  }, [isAuthenticated, contextsEnabled, selectedContextId, contexts, activeTab]);

  // Create a context-aware fetchTasks wrapper for pages that need context filtering
  // This ensures that when tasks are updated (e.g., toggling active state), 
  // the correct context filter is maintained
  const getContextAwareFetchTasks = (forTab) => {
    return async () => {
      if (contextsEnabled && (forTab === 'storage' || forTab === 'archive' || forTab === 'tags')) {
        const contextToUse = selectedContextId || (contexts.length > 0 ? contexts[0].id : null);
        if (contextToUse) {
          await fetchTasks(contextToUse);
        }
      } else {
        await fetchTasks();
      }
    };
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      );
    }

    switch (activeTab) {
      case 'active':
        return <ActivePage tasks={tasks} fetchTasks={getContextAwareFetchTasks('active')} tags={tags} dragHandlePosition={dragHandlePosition} onAddTaskForList={(list) => { setAddTaskList(list); setShowAddTask(true); }} />;
      case 'storage':
        return <StoragePage tasks={tasks} fetchTasks={getContextAwareFetchTasks('storage')} tags={tags} dragHandlePosition={dragHandlePosition} />;
      case 'archive':
        return <ArchivePage tasks={tasks} fetchTasks={getContextAwareFetchTasks('archive')} tags={tags} />;
      case 'tags':
        return <TagsPage tags={tags} fetchTags={fetchTags} dragHandlePosition={dragHandlePosition} />;
      case 'admin':
        return <AdminPage />;
      default:
        return <ActivePage tasks={tasks} fetchTasks={getContextAwareFetchTasks('active')} tags={tags} dragHandlePosition={dragHandlePosition} />;
    }
  };

  return (
    <div className="App min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-28 md:pb-0">
      <TopBar
        activeTab={activeTab}
        onChange={setActiveTab}
        onAboutClick={() => setShowAbout(true)}
        onSettingsClick={() => setShowSettings(true)}
        onAddClick={() => { setAddTaskList(null); setShowAddTask(true); }}
        user={user}
      />
      {renderPage()}
      <AddTaskDialog
        open={showAddTask}
        onOpenChange={(open) => { setShowAddTask(open); if (!open) setAddTaskList(null); }}
        onTaskAdded={getContextAwareFetchTasks(activeTab)}
        tags={tags}
        defaultActive={activeTab === 'active'}
        defaultActiveListId={addTaskList?.id || null}
        defaultContextId={addTaskList?.context_id || null}
        prefilledTag={
          addTaskList?.domain_tag_id
            ? (() => {
                const t = tags.find((tag) => tag.id === addTaskList.domain_tag_id);
                return t ? { category: 'Domain', value: `${t.emoji || ''} ${t.name}`.trim() } : null;
              })()
            : null
        }
      />
      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} />
      <AboutDialog open={showAbout} onOpenChange={setShowAbout} />
      <Toaster 
        position="bottom-center"
        toastOptions={{
          classNames: {
            toast: 'md:!bottom-0 !top-16 md:!top-auto group toast !mx-auto',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SettingsProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/app/*" element={<AppContent />} />
            {/* Redirect old routes to home */}
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </Routes>
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
